import { useState, useEffect } from 'react';
import api from '../services/api';

// Module-level cache (persists across component mounts)
let cachedData = null;
let fetchPromise = null;

/**
 * Hook to fetch and cache vehicle reference data from the API.
 * Data is fetched once and cached at module level.
 *
 * @returns {{ referenceData: Object|null, loading: boolean, error: string }}
 */
const useReferenceData = () => {
  const [referenceData, setReferenceData] = useState(cachedData);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState('');

  useEffect(() => {
    if (cachedData) {
      setReferenceData(cachedData);
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = Promise.all([
        api.get('/reference/body-types'),
        api.get('/reference/fuel-types'),
        api.get('/reference/transmissions'),
        api.get('/reference/colors'),
        api.get('/reference/drive-types'),
        api.get('/reference/interior-types'),
        api.get('/reference/paint-types'),
        api.get('/reference/euro-standards'),
      ])
        .then(([bodyTypes, fuelTypes, transmissions, colors, driveTypes, interiorTypes, paintTypes, euroStandards]) => {
          cachedData = {
            bodyTypes: bodyTypes.data,
            fuelTypes: fuelTypes.data,
            transmissions: transmissions.data,
            colors: colors.data,
            driveTypes: driveTypes.data,
            interiorTypes: interiorTypes.data,
            paintTypes: paintTypes.data,
            euroStandards: euroStandards.data,
          };
          return cachedData;
        })
        .catch(() => {
          fetchPromise = null;
          return null;
        });
    }

    fetchPromise.then((data) => {
      if (data) {
        setReferenceData(data);
      } else {
        setError('Erreur lors du chargement des données de référence');
      }
      setLoading(false);
    });
  }, []);

  return { referenceData, loading, error };
};

/**
 * Search makes by name (debounced calls recommended).
 * @param {string} search - Search query
 * @returns {Promise<Array<{key, name, models}>>}
 */
export const searchMakes = async (search) => {
  if (!search || search.length < 1) return [];
  try {
    const response = await api.get(`/reference/makes?search=${encodeURIComponent(search)}`);
    return response.data;
  } catch {
    return [];
  }
};

/**
 * Get models for a specific make.
 * @param {string} makeKey - The make key
 * @returns {Promise<Array<{key, name}>>}
 */
export const getModelsForMake = async (makeKey) => {
  if (!makeKey) return [];
  try {
    const response = await api.get(`/reference/makes/${encodeURIComponent(makeKey)}/models`);
    return response.data;
  } catch {
    return [];
  }
};

export default useReferenceData;
